import React, { useState } from 'react';
import styled from 'styled-components';
import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import { PrimaryButton, SecondaryButton } from '../Button';
import { ICohortRow } from '../../redux/slices/oldBuilder';
import printGraphqlErrors from '../../utils/apollo/printGraphqlErrors';
import {
  COHORT_NAME_MAX_LENGTH,
  DESCRIPTION_MAX_LENGTH,
} from '../../config/constants';
import {
  CohortType,
  IBasicCohortInput,
  ICohortFieldsFragment,
  IManageVersionEntityMutation,
  IManageVersionEntityMutationVariables,
  INamedCohortCreateMutation,
  INamedCohortCreateMutationVariables,
  INamedCohortEditMutation,
  INamedCohortEditMutationVariables,
  ManagedEntityType,
  PermissionKey,
} from '../../utils/apollo/graphqlSchema';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import {
  closeModal,
  ModalSliceType,
  openModal,
} from '../../redux/slices/modal';
import { useStrategyOptions } from '../../utils/hooks';
import KubitModal from '../KubitModal';
import RadioGroup from '../RadioGroup';
import {
  CommentField,
  DescriptionField,
  NameField,
  useValidateNameField,
  VerticalField,
  FieldLabel,
} from '../../components/Form';
import {
  CREATE_NAMED_COHORT,
  EDIT_NAMED_COHORT,
  GET_COHORT_EXECUTE_CONFIG,
  GET_NAMED_COHORTS,
} from '../../utils/apollo/cohorts';
import { ModalActions } from '../KubitModal/KubitModalUI';
import SaveEntityHeader, { SaveEntityHeaderEnum } from '../SaveEntityHeader';
import { NamedCohortsMenu } from '../Builder/Cohorts';
import {
  activeSchemaIdSelector,
  rowSelector,
} from '../../redux/selectors/builder';
import {
  parseCohortToBuilder,
  prepareBasicCohortInput,
} from '../../utils/builder/cohorts';
import { defaultCohortRow } from '../../redux/slices/oldBuilder/defaults';
import usePermission from '../../utils/hooks/permissionHooks/usePermission';
import PermissionElementWrapper from '../../pages/PermissionControls/PermissionElementWrapper';
import { SEARCH_TAGS } from '../../utils/apollo/entitySelection/query';
import { ApplyTags } from '../Categories';
import { useSaveCategories } from '../../utils/categories';
import toastMessage from '../../utils/toastMessage';
import { appRoutes } from '../../path';
import {
  getUrlForDataSharing,
  SearchParamsDataSharingEnum,
} from '../../utils/searchParamsDataSharing';
import { MANAGE_VERSION_ENTITY } from '../../utils/apollo/manageView/mutations';
import {
  assertCondition,
  assertNotNil,
  ICreatableReactSelectOption,
} from '../../utils/types';
import { defaultManagedOption } from '../Categories/utils';
import { generateCohortRowFromCachedCohort } from '../../utils/builder';
import { getCohortPagePath } from '../../utils';
import useQuickAccessDictionaryViewState from '../../redux/selectorHooks/useQuickAccessDictionaryViewState';
import { clearCachedCohort } from '../../redux/slices/cachedEntities';
import useCachedEntitiesSelector from '../../redux/selectorHooks/useCachedEntitiesSelector';
import { useCurrentUrl } from '../../utils/routing';
import { selectModalState } from '../../redux/selectors/modal';
import { UnreachableCaseError } from 'ts-essentials';
import ThinDivider from '../ThinDivider';

/**
 * This component covers several features:
 * 1. Save Simple cohort row (references);
 * 2. Save Cohort builder with simple cohort
 * 3. Save Cohort builder with compound cohort
 */

const CommentFormGroup = styled(CommentField)`
  margin-top: 24px;
`;

const StrategyOptions = styled(RadioGroup)`
  margin-bottom: 30px;
`;
const TagsContainer = styled.div`
  margin-bottom: 24px;
`;

interface IProps {
  onSubmit?: (cohort: ICohortFieldsFragment) => void;
}

export default ({ onSubmit }: IProps) => {
  const { isReportInUrl, isCreateCohortInUrl } = useCurrentUrl();
  const cachedCohort = useCachedEntitiesSelector('cachedCohort');
  const activeSchemaId = useAppSelector(activeSchemaIdSelector);

  // if cohort is created from report, we use the active schema id
  // if cohort is created from dashboard / workspace, we use the schema id of the interacting report
  const creatingCohortSchemaId =
    isReportInUrl || isCreateCohortInUrl
      ? activeSchemaId
      : cachedCohort?.schemaId;
  assertNotNil(creatingCohortSchemaId);

  const { data: cohortExecuteConfig, loading: isLoadingCohortExecuteConfig } =
    useQuery(GET_COHORT_EXECUTE_CONFIG, {
      variables: { schemaId: creatingCohortSchemaId },
    });
  const cohortConfig = cohortExecuteConfig?.cohortExecuteConfig?.cohortConfig;
  const { checkIsPermitted } = usePermission();
  const isPermittedToReadCohort = checkIsPermitted({
    requiredPermissionKeys: [PermissionKey.CohortRead],
    schemaId: creatingCohortSchemaId,
  });

  const isPermittedToCreateCohort = checkIsPermitted({
    requiredPermissionKeys: [PermissionKey.CohortCreate],
    schemaId: creatingCohortSchemaId,
  });

  const isPermittedToUpdateCohort = checkIsPermitted({
    requiredPermissionKeys: [PermissionKey.CohortEdit],
    schemaId: creatingCohortSchemaId,
  });
  const { strategy, setStrategy, strategyOptions } = useStrategyOptions({
    defaultStrategy: isPermittedToCreateCohort ? 'create' : 'update',
    permittedStatus: {
      create: isPermittedToCreateCohort,
      update: isPermittedToUpdateCohort,
    },
  });
  const isDictionarySidebarExpanded = useQuickAccessDictionaryViewState();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [comment, setComment] = useState('');
  const [namedCohortId, setNamedCohortId] = useState('');

  const dispatch = useAppDispatch();

  const modalData = useAppSelector(selectModalState);

  const { type, data } = modalData;

  const { rowMeta, shouldCohortImportToBraze, cohortBuilderInfo } = data;

  const client = useApolloClient();

  const currentCohortRow: ICohortRow = useAppSelector((state) =>
    rowSelector(state, rowMeta),
  );

  const onCloseModal = () => {
    setName('');
    setDescription('');
    dispatch(closeModal());
    dispatch(clearCachedCohort());
  };

  const handleCreateMutationComplete = ({
    namedCohortCreate,
  }: INamedCohortCreateMutation) => {
    const { entity, errors } = namedCohortCreate;

    if (printGraphqlErrors(errors)) {
      return;
    }

    if (entity) {
      // refetch latest cohorts for dictionary sidebar
      if (isDictionarySidebarExpanded) {
        client.refetchQueries({
          include: [SEARCH_TAGS],
        });
      }

      const redirectUrl = getUrlForDataSharing(appRoutes.cohorts.view, {
        type: SearchParamsDataSharingEnum.Dictionary,
        schemaId: creatingCohortSchemaId,
        term: name,
      });
      if (redirectUrl) {
        toastMessage({
          message: `Cohort Saved. "${name}"`,
          type: 'action',
          redirectPath: getCohortPagePath(entity.id),
        });
      }

      if (onSubmit) {
        onSubmit(entity);
      }

      onCloseModal();
      if (shouldCohortImportToBraze) {
        dispatch(
          openModal({ type: ModalSliceType.CohortImportToBraze, data: entity }),
        );
      }
    }
  };

  const [createNamedCohort, { loading: namedCohortIsCreating }] = useMutation<
    INamedCohortCreateMutation,
    INamedCohortCreateMutationVariables
  >(CREATE_NAMED_COHORT, {
    onCompleted: handleCreateMutationComplete,
  });

  const [manageVersionedEntity] = useMutation<
    IManageVersionEntityMutation,
    IManageVersionEntityMutationVariables
  >(MANAGE_VERSION_ENTITY);

  const handleChangeTags = (values: ICreatableReactSelectOption[]) => {
    const managed = values.find(
      (value) => value.value === defaultManagedOption.value,
    );
    setIsEntityManaged(!!managed);
    setCategories(
      values.filter((value) => value.value !== defaultManagedOption.value),
    );
  };

  const handleManagedTag = async (cohortId: string) => {
    const res = await manageVersionedEntity({
      variables: {
        input: {
          entityId: cohortId,
          entityType: ManagedEntityType.Cohort,
          isManaged: true,
        },
      },
    });

    if (res.data?.manageVersionEntity?.errors) {
      printGraphqlErrors(res.data.manageVersionEntity.errors);
    }
  };

  const handleEditMutationComplete = ({
    namedCohortEdit,
  }: INamedCohortEditMutation) => {
    const { entity, errors } = namedCohortEdit;

    if (printGraphqlErrors(errors)) {
      return;
    }

    if (entity) {
      toastMessage({
        message: `Cohort Updated. "${name}"`,
        type: 'action',
        redirectPath: getCohortPagePath(entity.id),
      });

      if (onSubmit) {
        onSubmit(entity);
      }

      onCloseModal();

      if (shouldCohortImportToBraze) {
        dispatch(
          openModal({ type: ModalSliceType.CohortImportToBraze, data: entity }),
        );
      }
    }
  };

  const [editNamedCohort, { loading: namedCohortIsUpdating }] = useMutation<
    INamedCohortEditMutation,
    INamedCohortEditMutationVariables
  >(EDIT_NAMED_COHORT, {
    onCompleted: handleEditMutationComplete,
  });

  const {
    categories,
    setCategories,
    prepareCategories,
    isEntityManaged,
    setIsEntityManaged,
  } = useSaveCategories(creatingCohortSchemaId);

  const { isValid: isValidCohortName } = useValidateNameField({
    draftName: name,
    limit: COHORT_NAME_MAX_LENGTH,
    originalName: '',
  });

  const prepareMutationBasicCohortInput = (): IBasicCohortInput => {
    assertCondition(
      type === ModalSliceType.SaveCohortFromAnalysisId ||
        type === ModalSliceType.SaveCohort ||
        type === ModalSliceType.SaveCohortRow,
    );
    // Case 1: Preparing to save cohort from analysis
    if (type === ModalSliceType.SaveCohortFromAnalysisId) {
      assertNotNil(cohortBuilderInfo);
      const cohortInfo = cohortBuilderInfo as ICohortFieldsFragment;

      const { id } = cohortInfo;

      if (cohortInfo.__typename === 'CompoundCohort') {
        const { simpleCohorts, logicOps } = cohortInfo;

        return prepareBasicCohortInput({
          type: CohortType.Compound,
          id,
          cohortRows: simpleCohorts.map((simpleCohort) => ({
            ...defaultCohortRow(),
            ...parseCohortToBuilder(simpleCohort),
          })),
          logicOpKey: logicOps[0].key,
          schemaId: creatingCohortSchemaId,
        });
      } else {
        return prepareBasicCohortInput({
          type: CohortType.Simple,
          cohortRow: {
            ...defaultCohortRow(),
            ...parseCohortToBuilder(cohortInfo),
          },
          schemaId: creatingCohortSchemaId,
        });
      }
    } else if (type === ModalSliceType.SaveCohort) {
      // Case 2: when type is ModalSliceType.SaveCohort, the modal is opened from analysis,
      // either from chart contextual menu, or measure/groups/etcs.
      // in this case, we need to prepare the cohort input based on cached cohort data.
      assertNotNil(cachedCohort);
      const builderCohort = generateCohortRowFromCachedCohort(
        cachedCohort,
        cohortConfig,
      );
      return prepareBasicCohortInput({
        type: CohortType.Simple,
        cohortRow: builderCohort,
        schemaId: creatingCohortSchemaId,
      });
    } else if (type === ModalSliceType.SaveCohortRow) {
      // Case 3: Preparing to save cohort from cohort row
      // When saving from cohort builder, we have to
      // take main settings from builderSampledCohort
      return prepareBasicCohortInput({
        type: CohortType.Simple,
        cohortRow: {
          ...currentCohortRow,
        },
        schemaId: creatingCohortSchemaId,
      });
    } else {
      throw new UnreachableCaseError(type);
    }
  };

  const handleCreate = async () => {
    const inputCategories = await prepareCategories();

    const { data } = await createNamedCohort({
      variables: {
        input: {
          cohort: prepareMutationBasicCohortInput(),
          comment: comment.trim(),
          description: description.trim(),
          name: name.trim(),
          schemaId: creatingCohortSchemaId,
          categories: inputCategories,
        },
      },
    });

    if (data) {
      // Refetch the cohorts query so the BuilderCohortSelector is up-to-date.
      await client.refetchQueries({
        include: [GET_NAMED_COHORTS],
      });

      if (isEntityManaged && data.namedCohortCreate?.entity?.id) {
        handleManagedTag(data.namedCohortCreate.entity.id);
      }
    }
  };

  const handleUpdate = async () => {
    const inputCategories = await prepareCategories();

    const { data } = await editNamedCohort({
      variables: {
        input: {
          name: null,
          cohort: prepareMutationBasicCohortInput(),
          comment: comment.trim(),
          description: description.trim(),
          id: namedCohortId,
          categories: inputCategories,
        },
      },
    });

    if (data) {
      // Refetch the cohorts query so the BuilderCohortSelector is up-to-date.
      await client.refetchQueries({
        include: [GET_NAMED_COHORTS],
      });

      if (isEntityManaged && data.namedCohortEdit?.entity?.id) {
        handleManagedTag(data.namedCohortEdit.entity.id);
      }
    }
  };

  const resetForm = () => {
    if (name) {
      setName('');
    }

    if (description) {
      setDescription('');
    }

    if (namedCohortId) {
      setNamedCohortId('');
    }

    if (categories.length > 0) {
      setCategories([]);
    }
  };

  const handleChangeStrategy = (newStrategy: string) => {
    setStrategy(newStrategy);

    resetForm();
  };

  const handleSelectNamedCohort = (cohort: ICohortFieldsFragment) => {
    setNamedCohortId(cohort.id);

    const version = cohort?.version;

    if (version) {
      const { name, description } = version;

      setName(name);
      setDescription(description || '');
      setCategories(
        cohort?.categories?.map((cat) => ({
          label: cat.name,
          value: cat.id,
        })) ?? [],
      );

      if (cohort.isManaged) {
        setIsEntityManaged(true);
      }
    }
  };

  const isDescriptionLengthValid =
    description.trim().length <= DESCRIPTION_MAX_LENGTH;
  const isCommentLengthValid = comment.trim().length <= DESCRIPTION_MAX_LENGTH;

  return (
    <KubitModal open onClose={onCloseModal} header="Save Cohort">
      <SaveEntityHeader
        rowMeta={rowMeta}
        entityType={SaveEntityHeaderEnum.NamedCohort}
      />
      <StrategyOptions
        name="strategy"
        value={strategy}
        onChange={handleChangeStrategy}
        options={strategyOptions}
      />
      {strategy === 'create' ? (
        <NameField
          label={'Name'}
          value={name}
          onChange={setName}
          placeholder="Enter a new cohort name"
          targetErrorElement={'Cohort'}
          maxLength={COHORT_NAME_MAX_LENGTH}
          disabled={namedCohortIsCreating}
        />
      ) : (
        <VerticalField>
          <FieldLabel>Name</FieldLabel>
          <PermissionElementWrapper isPermitted={isPermittedToReadCohort}>
            <NamedCohortsMenu
              onChange={handleSelectNamedCohort}
              includeCompound
              onClear={resetForm}
              defaultNamedCohort={null}
              isDisabled={!isPermittedToReadCohort}
            />
          </PermissionElementWrapper>
        </VerticalField>
      )}
      <DescriptionField
        label={'Description'}
        value={description}
        onChange={setDescription}
        placeholder="Add cohort description here"
        targetErrorElement={'Cohort'}
        disabled={namedCohortIsCreating || namedCohortIsUpdating}
      />
      <TagsContainer>
        <ApplyTags
          schemaId={creatingCohortSchemaId}
          selectedValues={categories}
          onChange={handleChangeTags}
          isManaged={isEntityManaged}
        />
      </TagsContainer>
      <ThinDivider layout="horizontal" size="full" />
      <CommentFormGroup
        label={'Comment'}
        value={comment}
        onChange={setComment}
        placeholder="Add your comment here"
        targetErrorElement={'Cohort'}
        disabled={namedCohortIsCreating || namedCohortIsUpdating}
      />
      <ModalActions>
        <SecondaryButton
          type="button"
          onClick={onCloseModal}
          disabled={namedCohortIsCreating || namedCohortIsUpdating}
          trackContext="cohorts"
          trackAction="cancel"
        >
          Cancel
        </SecondaryButton>
        {strategy === 'update' ? (
          <PrimaryButton
            type="button"
            onClick={handleUpdate}
            disabled={
              namedCohortIsUpdating ||
              !namedCohortId ||
              isLoadingCohortExecuteConfig ||
              !isDescriptionLengthValid ||
              !isCommentLengthValid
            }
            trackContext="cohorts"
            trackAction="update"
          >
            Update
          </PrimaryButton>
        ) : (
          <PrimaryButton
            type="button"
            onClick={handleCreate}
            disabled={
              !isValidCohortName ||
              namedCohortIsCreating ||
              isLoadingCohortExecuteConfig ||
              !isDescriptionLengthValid ||
              !isCommentLengthValid
            }
            trackContext="cohorts"
            trackAction="create"
          >
            Create
          </PrimaryButton>
        )}
      </ModalActions>
    </KubitModal>
  );
};
